import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Search, Users, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES } from "@/lib/categories";
import { getUser } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const choices = (categoryId) => [
  {
    to: `/annonces?cat=${categoryId}`,
    icon: Megaphone,
    title: "Déposer une annonce",
    subtitle: "Publiez votre annonce dans cette catégorie",
    testid: "choix-deposer",
  },
  {
    to: `/categorie/${categoryId}/annonces`,
    icon: Search,
    title: "Consulter une annonce",
    subtitle: "Parcourez les annonces déjà publiées",
    testid: "choix-consulter",
  },
];

export default function ChoixCategorie() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const cat = CATEGORIES[categoryId];
  if (!cat) return <Navigate to="/" replace />;

  const allerDeposer = async (e) => {
    if (categoryId !== "love") return;
    e.preventDefault();
    const user = getUser();
    if (!user) {
      toast.error("Créez d'abord votre profil pour déposer une annonce.");
      navigate("/inscription");
      return;
    }
    try {
      const res = await axios.get(`${API}/profiles/${user.id}`);
      if (!res.data.profilDetaille) {
        toast.info("Complétez d'abord les informations de votre profil FodindinLove.");
        navigate(`/inscription/love/etape2?next=${encodeURIComponent("/annonces?cat=love")}`);
        return;
      }
      navigate("/annonces?cat=love");
    } catch {
      navigate("/inscription");
    }
  };

  return (
    <div className="min-h-screen bg-[#12100E] flex flex-col">
      <Navbar />
      <main className="relative flex-1 flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-[150px]" style={{ background: cat.color, opacity: 0.35 }} />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full blur-[150px]" style={{ background: cat.color, opacity: 0.18 }} />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-3xl mx-auto px-4 py-20 text-center"
        >
          <span
            className="inline-block text-[11px] font-bold tracking-[0.25em] uppercase px-4 py-2 rounded-full border mb-8"
            style={{ color: cat.color, borderColor: `${cat.color}50`, backgroundColor: `${cat.color}15` }}
            data-testid="choix-badge-categorie"
          >
            {cat.name}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-stone-50 max-w-xl mx-auto">
            Que souhaitez-vous faire dans cette catégorie ?
          </h1>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto">
            {choices(categoryId).map((c, i) => (
              <motion.div
                key={c.testid}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}
              >
                <Link
                  to={c.to}
                  data-testid={c.testid}
                  onClick={c.testid === "choix-deposer" ? allerDeposer : undefined}
                  className="group block h-full bg-white/[0.06] backdrop-blur-md border-2 border-white/15 hover:border-white/40 rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1.5"
                >
                  <c.icon size={40} strokeWidth={1.4} className="mx-auto mb-5" style={{ color: cat.color }} />
                  <div className="font-heading text-lg font-bold text-stone-50">{c.title}</div>
                  <div className="text-sm text-stone-400 mt-2 leading-relaxed">{c.subtitle}</div>
                </Link>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-10 flex items-center justify-center gap-6 text-sm"
          >
            <Link to={`/categorie/${categoryId}`} data-testid="choix-voir-profils" className="inline-flex items-center gap-2 text-stone-400 hover:text-white transition-colors">
              <Users size={15} /> Voir les profils membres
            </Link>
            <Link to="/" data-testid="choix-retour" className="inline-flex items-center gap-2 text-stone-500 hover:text-white transition-colors">
              <ArrowLeft size={15} /> Accueil
            </Link>
          </motion.div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
